import {ArrowRight} from 'lucide-react';
import {Link} from 'react-router-dom';
import {Button, Card, Separator} from '@tinct/web-components';
import {StepButton, type StepButtonState} from '@tinct/web-components';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import type {ReadCampaign} from '@tinct/core-api-models';
import {
  type CampaignSetupStep,
  getCampaignSetupStep,
} from '../../../funnels/campaign-creation-funnel/get-campaign-setup-step';

const SETUP_STEPS = [
  {label: 'Configure campaign', number: 1},
  {label: 'Define audience', number: 2},
  {label: 'Preview & Refine', number: 3},
  {label: 'Publish', number: 4},
  {label: 'Connect', number: 5},
] as const;

// The NEW-origin funnel is shorter: configure → building → edit → live.
const NEW_SETUP_STEPS = [
  {label: 'Configure campaign', number: 1},
  {label: 'Edit page', number: 2},
  {label: 'Launch', number: 3},
] as const;

// ADWORD-axis campaigns run the normal funnel minus audience/sampling —
// variants come from synced ads campaigns (Google Ads / LinkedIn Ads), generated on match.
const ADWORD_SETUP_STEPS = [
  {label: 'Configure campaign', number: 1},
  {label: 'Preview & Refine', number: 2},
  {label: 'Publish', number: 3},
  {label: 'Connect', number: 4},
] as const;

// The stepper is a coarser projection of the funnel's granular steps.
const STEP_NUMBER: Record<CampaignSetupStep, number> = {
  configure: 1,
  analyze: 1,
  building: 1,
  edit: 2,
  audience: 2,
  sampling: 2,
  preview: 3,
  publish: 4,
  publishing: 4,
  live: 5,
};

const NEW_STEP_NUMBER: Partial<Record<CampaignSetupStep, number>> = {
  configure: 1,
  building: 1,
  edit: 2,
  live: 3,
};

const ADWORD_STEP_NUMBER: Partial<Record<CampaignSetupStep, number>> = {
  configure: 1,
  analyze: 1,
  preview: 2,
  publish: 3,
  publishing: 3,
  live: 4,
};

function StepSeparator() {
  return (
    <div className="flex h-5 w-6 items-center justify-center">
      <div className="h-px flex-1 border-t border-ring/40" />
    </div>
  );
}

export type CampaignSetupProgressProps = {
  campaign: ReadCampaign;
};

export function CampaignSetupProgress({campaign}: CampaignSetupProgressProps) {
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const isNewOrigin = campaign.landingPageOriginType === 'new';
  const isAdwordAxis = campaign.landingPageVariationAxis === 'adword';
  const setupStep = getCampaignSetupStep(campaign);
  // Origin wins: an adword NEW-origin campaign runs the NEW phase-1 funnel
  // (configure → building → edit → live); the ADWORD stepper covers the
  // existing-origin chain without audience/sampling.
  const steps = isNewOrigin
    ? NEW_SETUP_STEPS
    : isAdwordAxis
      ? ADWORD_SETUP_STEPS
      : SETUP_STEPS;
  const activeStep =
    (isNewOrigin
      ? NEW_STEP_NUMBER[setupStep]
      : isAdwordAxis
        ? ADWORD_STEP_NUMBER[setupStep]
        : STEP_NUMBER[setupStep]) ?? 1;

  const funnelUrl = `${workspaceUrl}/funnel-campaigns/${campaign.id}`;

  return (
    <Card className="flex flex-col rounded-2xl p-0">
      {/* Header */}
      <div className="flex flex-col gap-1.5 px-6 pt-6">
        <h3 className="font-heading text-xl font-semibold tracking-tight">
          Welcome
        </h3>
        <p className="text-sm text-muted-foreground">
          Complete your setup to start personalizing landing pages
        </p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-0 px-6 py-6">
        {steps.map((step, index) => {
          let state: StepButtonState;
          if (step.number < activeStep) state = 'filled';
          else if (step.number === activeStep) state = 'active';
          else state = 'disabled';

          return (
            <div key={step.number} className="flex items-center">
              {index > 0 && <StepSeparator />}
              <StepButton
                label={step.label}
                number={step.number}
                state={state}
                className="flex shrink-0 flex-row items-center gap-2"
              />
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <Separator />
      <div className="flex items-center justify-end px-6 py-4">
        <Button asChild>
          <Link to={funnelUrl}>
            Continue setup
            <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>
    </Card>
  );
}
